<template>
  <div v-if="store.state.role === 'admin'" class="ms-send">
    <el-row :gutter="20">
      <el-col :span="24">
        <el-card shadow="never" class="ms-send__card">
          <template #header>
            <span class="ms-send__card-title">Fayl formati</span>
          </template>

          <el-table :data="columns" size="small" border class="ms-send__table">
            <el-table-column prop="col" label="Ustun" width="64" align="center" />
            <el-table-column prop="field" label="Maydon" />
            <el-table-column prop="example" label="Misol" />
          </el-table>

          <ul class="ms-send__rules">
            <li>1-qator sarlavha sifatida o'qilmaydi, ma'lumot 2-qatordan.</li>
            <li>Ustunlar ketma-ketligi muhim, sarlavha nomi emas.</li>
            <li>5 ta ustun ham majburiy, <b>Soato</b> — faqat raqam.</li>
            <li>
              Xato qatorlar bo'lsa — xatoliklar hisoboti (.xlsx) avtomatik
              yuklab olinadi, to'g'ri qatorlar esa yuborilgan bo'ladi.
            </li>
            <li>
              Bir faylni ikki marta yubormang — takroriy qatorlar Invest
              tizimiga ikki marta tushadi.
            </li>
          </ul>

          <el-alert
            type="info"
            :closable="false"
            show-icon
            title="Shablon CSV formatida yuklanadi — to'ldirgach faylni Excel'da &quot;Excel Workbook (.xlsx)&quot; sifatida saqlang."
          />
        </el-card>
      </el-col>

      <el-col :span="24">
        <el-card shadow="never" class="ms-send__card">
          <template #header>
            <div class="ms-send__card-head">
              <span class="ms-send__card-title">Excel faylni yuborish</span>
              <el-button
                text
                type="primary"
                :icon="Download"
                @click="downloadTemplate"
              >
                Shablon
              </el-button>
            </div>
          </template>

          <el-upload
            ref="uploadRef"
            class="ms-send__upload"
            action="#"
            :auto-upload="false"
            :multiple="false"
            accept=".xlsx, .xls"
            :limit="1"
            :show-file-list="false"
            :disabled="loading"
            :on-exceed="onExceed"
            :on-change="onFileChange"
            drag
          >
            <el-icon class="el-icon--upload"><upload-filled /></el-icon>
            <div class="el-upload__text">
              Faylni bu yerga tashlang yoki <em>tanlash uchun bosing</em>
            </div>
            <template #tip>
              <div class="el-upload__tip">
                Faqat .xlsx yoki .xls format, bitta fayl
              </div>
            </template>
          </el-upload>

          <div v-if="file" class="ms-send__file">
            <el-icon class="ms-send__file-icon"><Document /></el-icon>
            <div class="ms-send__file-meta">
              <div class="ms-send__file-name">{{ file.name }}</div>
              <div class="ms-send__file-size">{{ fileSize }}</div>
            </div>
            <el-button
              text
              type="danger"
              :icon="Close"
              :disabled="loading"
              @click="resetUpload"
            />
          </div>

          <div class="ms-send__actions">
            <el-button
              type="primary"
              size="large"
              :loading="loading"
              :disabled="!file"
              @click="send"
            >
              {{ loading ? "Yuborilmoqda..." : "Invest tizimiga yuborish" }}
            </el-button>
            <span v-if="loading" class="ms-send__progress-note">
              Har bir qator ketma-ket yuboriladi — bu bir necha daqiqa olishi
              mumkin. Sahifani yopmang.
            </span>
          </div>

          <el-alert
            v-if="result"
            class="ms-send__result"
            :type="result.type"
            :title="result.title"
            :description="result.description"
            :closable="true"
            show-icon
            @close="result = null"
          />
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { useStore } from "vuex";
import { ElNotification, ElMessage } from "element-plus";
import { Close, Document, Download, UploadFilled } from "@element-plus/icons-vue";
import http from "@/http";

const store = useStore();

const columns = [
  { col: "A", field: "Soato (hudud kodi)", example: "1703227" },
  { col: "B", field: "Tavsif (loyiha nomi)", example: "1-maktab binosini qurish" },
  { col: "C", field: "Maktab STIR", example: "301234567" },
  { col: "D", field: "Buyurtmachi nomi", example: "Toshkent shahar XTB" },
  { col: "E", field: "Buyurtmachi STIR", example: "201234567" },
];

const uploadRef = ref(null);
const file = ref(null);
const loading = ref(false);
const result = ref(null);

const fileSize = computed(() => {
  if (!file.value) return "";
  const kb = file.value.size / 1024;
  return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.ceil(kb)} KB`;
});

const onFileChange = (uploadFile) => {
  file.value = uploadFile.raw || null;
  result.value = null;
};

const onExceed = (files) => {
  uploadRef.value?.clearFiles();
  uploadRef.value?.handleStart(files[0]);
  file.value = files[0];
  result.value = null;
};

const resetUpload = () => {
  file.value = null;
  uploadRef.value?.clearFiles();
};

const parseFilename = (disposition) => {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match?.[1] || `myschool_errors_${Date.now()}.xlsx`;
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Javob yoki JSON, yoki .xlsx hisobot fayli — shuning uchun har doim blob
// olib, "content-disposition" bo'yicha ajratamiz.
const handleResponse = async (response) => {
  const disposition = response.headers["content-disposition"] || "";

  if (disposition.toLowerCase().includes("attachment")) {
    const filename = parseFilename(disposition);
    downloadBlob(
      new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      filename
    );
    result.value = {
      type: "warning",
      title: "Ba'zi qatorlarda xatolik topildi",
      description: `Xatoliklar hisoboti yuklab olindi (${filename}). Har bir qator sababi "Error" ustunida. To'g'ri qatorlar Invest tizimiga yuborilgan — hisobotdagi qatorlarni tuzatib, faqat ularni qayta yuboring.`,
    };
    ElNotification({
      title: "Ogohlantirish",
      message: "Xatoliklar hisoboti yuklab olindi",
      type: "warning",
    });
    return;
  }

  let body = {};
  try {
    body = JSON.parse(await response.data.text());
  } catch (e) {
    body = {};
  }

  if (response.status >= 200 && response.status < 300) {
    result.value = {
      type: "success",
      title: "Barcha qatorlar muvaffaqiyatli yuborildi",
      description: "Fayldagi hamma qator Invest tizimiga qabul qilindi.",
    };
    ElNotification({
      title: "Muvaffaqiyat",
      message: "Fayl muvaffaqiyatli yuborildi",
      type: "success",
    });
    resetUpload();
    return;
  }

  const message =
    body.message || `So'rov bajarilmadi (status ${response.status})`;
  result.value = { type: "error", title: "Xatolik", description: message };
  ElNotification({ title: "Xatolik", message, type: "error" });
};

const send = () => {
  if (!file.value) return;

  const formData = new FormData();
  formData.append("file", file.value);

  loading.value = true;
  result.value = null;
  http
    .post("api/v1/chronology/invest/myschool/send", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      responseType: "blob",
      timeout: 0,
    })
    .then(handleResponse)
    .catch(async (error) => {
      if (error.response) {
        await handleResponse(error.response);
        return;
      }
      const message = "Fayl yuborishda xatolik sodir bo'ldi";
      result.value = { type: "error", title: "Xatolik", description: message };
      ElNotification({ title: "Xatolik", message, type: "error" });
    })
    .finally(() => {
      loading.value = false;
    });
};

const downloadTemplate = () => {
  const csv = "﻿" + columns.map((c) => c.field).join(",") + "\n";
  downloadBlob(
    new Blob([csv], { type: "text/csv;charset=utf-8" }),
    "myschool-invest-shablon.csv"
  );
  ElMessage.success("Shablon yuklab olindi");
};
</script>

<style scoped lang="scss">
.ms-send {
  max-width: 900px;

  &__card {
    margin-bottom: 20px;
    border-radius: 8px;
  }

  &__card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__card-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }

  &__upload {
    :deep(.el-upload),
    :deep(.el-upload-dragger) {
      width: 100%;
    }
  }

  &__file {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
    padding: 12px 14px;
    border: 1px solid #e4e7ed;
    border-radius: 6px;
    background: #f7f9fc;
  }

  &__file-icon {
    font-size: 22px;
    color: #409eff;
  }

  &__file-meta {
    flex: 1;
    min-width: 0;
  }

  &__file-name {
    font-size: 14px;
    color: #303133;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__file-size {
    font-size: 12px;
    color: #909399;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
    margin-top: 20px;
  }

  &__progress-note {
    font-size: 13px;
    color: #e6a23c;
    line-height: 1.4;
  }

  &__result {
    margin-top: 20px;
  }

  &__table {
    margin-bottom: 16px;
  }

  &__rules {
    margin: 0 0 16px;
    padding-left: 18px;
    color: #606266;
    font-size: 13px;
    line-height: 1.7;

    li + li {
      margin-top: 2px;
    }
  }
}
</style>
